import {
	type ComponentType,
	lazy,
	SetStateAction,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	ApolloClient,
	ApolloLink,
	HttpLink,
	InMemoryCache,
	Operation,
	NextLink,
	// @ts-ignore - @apollo/client is ESM but this file is treated as CommonJS by TypeScript
} from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

import CommentIcon from '@atlaskit/icon/core/comment';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

// Load omni-channel UI only on the client when widget is open to avoid SSR 503 (Tesseract)
const ChatWidgetOmniChannelUI = lazy(
	() =>
		// @ts-expect-error - extension-less path; bundler resolves correctly
		import('./chat-widget-omni-channel-ui') as unknown as Promise<{
			default: ComponentType<ChatWidgetOmniChannelUIProps>;
		}>,
);

// Lazy-load analytics when fetchChatConnectionConfig succeeds (keeps @atlassian/omni-channel out of main bundle)
const ChatWidgetCustomerJoinedAnalytics = lazy(
	() =>
		// @ts-expect-error - extension-less path; bundler resolves correctly
		import('./chat-widget-customer-analytics') as unknown as Promise<{
			default: ComponentType<ChatWidgetCustomerJoinedAnalyticsProps>;
		}>,
);

// Lazy-load analytics when fetchToken fails (same chunk as customer-joined analytics)
const ChatWidgetTokenErrorAnalytics = lazy(
	() =>
		// @ts-expect-error - extension-less path; bundler resolves correctly
		import('./chat-widget-customer-analytics').then((m) => ({
			default: m.ChatWidgetTokenErrorAnalytics as ComponentType<ChatWidgetTokenErrorAnalyticsProps>,
		})) as unknown as Promise<{
			default: ComponentType<ChatWidgetTokenErrorAnalyticsProps>;
		}>,
);

// Lazy-load analytics when fetchChatConnectionConfig succeeds
const ChatWidgetConnectionConfigSuccessAnalytics = lazy(
	() =>
		// @ts-expect-error - extension-less path; bundler resolves correctly
		import('./chat-widget-customer-analytics').then((m) => ({
			default:
				m.ChatWidgetConnectionConfigSuccessAnalytics as ComponentType<ChatWidgetConnectionConfigSuccessAnalyticsProps>,
		})) as unknown as Promise<{
			default: ComponentType<ChatWidgetConnectionConfigSuccessAnalyticsProps>;
		}>,
);

// Lazy-load analytics when fetchToken succeeds (channelPlatformTokenError empty or null)
const ChatWidgetTokenSuccessAnalytics = lazy(
	() =>
		// @ts-expect-error - extension-less path; bundler resolves correctly
		import('./chat-widget-customer-analytics').then((m) => ({
			default:
				m.ChatWidgetTokenSuccessAnalytics as ComponentType<ChatWidgetTokenSuccessAnalyticsProps>,
		})) as unknown as Promise<{
			default: ComponentType<ChatWidgetTokenSuccessAnalyticsProps>;
		}>,
);

import { wacToken } from '@/libraries/design/foundations';
import { ClientOnly } from '@/utils/client-only';
import { Cookie } from '@/utils/cookie';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import {
	getChannelPlatformTokenByClientName,
	getChatConnectionConfigForSalesChannel,
	getCustomerConversations,
} from './ocp-gql';

import type {
	ChatWidgetConnectionConfigSuccessAnalyticsProps,
	ChatWidgetCustomerJoinedAnalyticsProps,
	ChatWidgetTokenErrorAnalyticsProps,
	ChatWidgetTokenSuccessAnalyticsProps,
} from './chat-widget-customer-analytics';
import type { ChatWidgetOmniChannelUIProps } from './chat-widget-omni-channel-ui';

/**
 * Converts a full WAC URL to a relative path segment (no domain, no hash).
 * - Strips optional leading /wac from the path.
 * - Examples:
 *   - https://...atlassian.com/wac/licensing/purchase-licensing#trials -> licensing/purchase-licensing
 *   - https://wac.stg.../software/compass/features -> software/compass/features
 */
export const getRelativePathFromWacUrl = (url: string): string => {
	try {
		const urlObj = new URL(url);
		let pathname = urlObj.pathname.replace(/\/+$/, '');

		if (pathname.startsWith('/wac/')) {
			pathname = pathname.slice('/wac'.length);
		}
		return pathname.replace(/^\/+/, '');
	} catch {
		const match = /\/\/[^/]+(\/[^#?]*)/.exec(url);
		if (match?.[1]) {
			let pathname = match[1].replace(/\/+$/, '');
			if (pathname.startsWith('/wac/')) {
				pathname = pathname.slice('/wac'.length);
			}
			return pathname.replace(/^\/+/, '');
		}
		return '';
	}
};

const CHAT_SUPPORT_CONFIG_BY_URL_PATH: Record<
	string,
	{
		contactFlowId: string;
		formKey: string[];
		attributes?: Record<string, string>;
	}
> = {
	'software/jira/product-discovery': {
		contactFlowId:
			process.env['JIRA_PRODUCT_DISCOVERY_CONTACT_FLOW_ID'] ||
			'4cdb6e29-9088-4404-bbfe-dfdba35bd3d6',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/jira/service-management': {
		contactFlowId:
			process.env['JIRA_PRODUCT_DISCOVERY_CONTACT_FLOW_ID'] ||
			'4cdb6e29-9088-4404-bbfe-dfdba35bd3d6',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/jira': {
		contactFlowId: process.env['JIRA_CONTACT_FLOW_ID'] || '7e776619-ede4-4975-a194-38c6b182fa0c',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/loom': {
		contactFlowId: process.env['LOOM_CONTACT_FLOW_ID'] || '42cee94e-c0db-43d2-bd45-3cca9d7adb61',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/loom/loom-confluence': {
		contactFlowId:
			process.env['LOOM_CONFLUENCE_CONTACT_FLOW_ID'] || '9f1756b6-8607-43b4-97b6-32f888713bf0',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/loom/loom-jira': {
		contactFlowId:
			process.env['LOOM_JIRA_CONTACT_FLOW_ID'] || '863e7f31-092a-45c6-affa-02db50c7ef72',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/loom/resources': {
		contactFlowId:
			process.env['LOOM_RESOURCES_CONTACT_FLOW_ID'] || '4b5e7c93-5e16-4479-8030-fcad737249a3',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/loom/ai-meeting': {
		contactFlowId:
			process.env['LOOM_AI_MEETING_CONTACT_FLOW_ID'] || 'f1956561-00b8-4ee0-a6c2-2100d07feb5f',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'software/loom/editing': {
		contactFlowId:
			process.env['LOOM_EDITING_CONTACT_FLOW_ID'] || 'bc21d930-ac0d-4eec-9158-d2dde2097029',
		formKey: ['ca-growth-pricing-billing-and-licensing'],
	},
	'ja/company/contact/japan': {
		contactFlowId:
			process.env['JA_COMPANY_CONTACT_JAPAN_CONTACT_FLOW_ID'] ||
			'c5be7db2-a892-4f58-a4ab-c79a04c59159',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
	'ja/software/jira/service-management': {
		contactFlowId:
			process.env['JA_SOFTWARE_JIRA_SERVICE_MANAGEMENT_CONTACT_FLOW_ID'] ||
			'c5be7db2-a892-4f58-a4ab-c79a04c59159',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
	'ja/software/jira/pricing': {
		contactFlowId:
			process.env['JA_SOFTWARE_JIRA_PRICING_CONTACT_FLOW_ID'] ||
			'c5be7db2-a892-4f58-a4ab-c79a04c59159',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
	ja: {
		contactFlowId: process.env['JA'] || 'c5be7db2-a892-4f58-a4ab-c79a04c59159',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
	'ja/software/confluence': {
		contactFlowId:
			process.env['JA_SOFTWARE_CONFLUENCE_CONTACT_FLOW_ID'] ||
			'c5be7db2-a892-4f58-a4ab-c79a04c59159',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
	'ja/software/confluence/pricing': {
		contactFlowId:
			process.env['JA_SOFTWARE_CONFLUENCE_PRICING_CONTACT_FLOW_ID'] ||
			'af93bfc1-9e6f-481b-bc07-834e30832ba6',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
	'ja/software/jira/service-management/pricing': {
		contactFlowId:
			process.env['JA_SOFTWARE_JIRA_SERVICE_MANAGEMENT_PRICING_CONTACT_FLOW_ID'] ||
			'c5be7db2-a892-4f58-a4ab-c79a04c59159',
		formKey: ['jpns-growth-pricing-billing-and-licensing'],
	},
};

export interface ContactFlowResult {
	contactFlowId: string;
}

/**
 * Returns an object with contactFlowId for a given relative path (e.g. "software/jira/product-discovery").
 */
export const getContactFlowByPath = (path: string): ContactFlowResult => {
	const normalizedPath = path.replace(/^\/+|\/+$/g, '');

	return CHAT_SUPPORT_CONFIG_BY_URL_PATH[normalizedPath] ?? { contactFlowId: '' };
};

export const getContactFormKeyByPath = (path: string): string[] => {
	const normalizedPath = path.replace(/^\/+|\/+$/g, '');
	const entry = CHAT_SUPPORT_CONFIG_BY_URL_PATH[normalizedPath];
	if (entry && Array.isArray(entry.formKey)) {
		return entry.formKey;
	}
	return [''];
};

export const getContactAttributesByPath = (path: string): string => {
	const normalizedPath = path.replace(/^\/+|\/+$/g, '');
	const entry = CHAT_SUPPORT_CONFIG_BY_URL_PATH[normalizedPath];
	if (entry && typeof entry.attributes === 'object' && entry.attributes !== null) {
		return JSON.stringify(entry.attributes, null, 2);
	}
	return '{}';
};

/** Returns true if the path is a Japan locale URL (e.g. "ja" or "ja/software/jira/pricing"). */
export const isJapanPath = (path: string): boolean => {
	const normalizedPath = path.replace(/^\/+|\/+$/g, '');
	return normalizedPath === 'ja' || normalizedPath.startsWith('ja/');
};

export const convertUrlToSnakeCase = (url: string): string => {
	try {
		const urlObj = new URL(url);
		const pathname = urlObj.pathname;

		// Remove leading and trailing slashes, then split by '/' and filter out empty strings
		const pathParts = pathname
			.replace(/^\/+|\/+$/g, '')
			.split('/')
			.filter((part) => part.length > 0);

		// Join with underscores
		return pathParts.join('_');
	} catch (error) {
		// If URL parsing fails, try to extract path manually
		const match = /\/\/[^\/]+(\/[^?#]*)/.exec(url);
		if (match && match[1]) {
			const pathname = match[1];
			const pathParts = pathname
				.replace(/^\/+|\/+$/g, '')
				.split('/')
				.filter((part) => part.length > 0);
			return pathParts.join('_');
		}
		return '';
	}
};

export const transformPayloadForChannelPlatform = (
	payload: Record<string, unknown>,
): Record<string, string[]> => {
	const transformed: Record<string, string[]> = {};
	if (!payload) {
		return transformed;
	}

	Object.entries(payload).forEach(([key, value]) => {
		// Skip attachments
		if (key === 'attachments') {
			return;
		}

		// Skip null or undefined values
		if (value === null || value === undefined) {
			return;
		}

		// Handle arrays - normalize to array of strings
		if (Array.isArray(value)) {
			transformed[key] = value.map((v) => {
				// If array element is an object (like {userEmail: "value"}), extract the first value
				if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
					const firstValue = Object.values(v)[0];
					return firstValue !== undefined ? String(firstValue) : String(v);
				}
				// Already a string or primitive
				return String(v);
			});
		}
		// Handle single values - convert to string and wrap in array
		else {
			transformed[key] = [String(value)];
		}
	});
	return transformed;
};

export const constructMetadata = (payload: Record<string, unknown>) => {
	return JSON.stringify(payload);
};

export type FormValues = {
	contactEmail?: string;
};

export const constructMetadataWithFlowId = (
	formValues: FormValues | null | undefined,
	flowId?: string,
	analyticsSessionId?: string,
): string => {
	const metadata: Record<string, unknown> = {};

	if (analyticsSessionId) {
		metadata['analyticsSessionId'] = analyticsSessionId;
	}

	// Only include flowId if it has a value
	if (flowId) {
		metadata['flowId'] = flowId;
	}

	if (formValues?.contactEmail) {
		metadata['email'] = formValues.contactEmail;
	}

	return JSON.stringify(metadata);
};

export const transformChatContactDetailsForSalesChannel = (chatConnectionConfigResponse: {
	channelPlatform_createContact?: unknown;
}) => {
	const submitRequest = chatConnectionConfigResponse?.channelPlatform_createContact;

	return {
		channelPlatform_submitRequest: submitRequest,
	};
};

export function getConversationIdFromChatConnectionConfig(
	config: { channelPlatform_createContact?: unknown } | null,
): string | undefined {
	const contact = config?.channelPlatform_createContact as
		| { value?: { contactId?: string } }
		| undefined;
	return contact?.value?.contactId;
}

export type ConnectionConfigAttributes = {
	contactId?: string;
	participantId?: string;
	region?: string;
};

export function getConnectionConfigAttributes(
	config: { channelPlatform_createContact?: unknown } | null,
): ConnectionConfigAttributes {
	const contact = config?.channelPlatform_createContact as
		| {
				value?: {
					contactId?: string;
					participantId?: string;
					region?: string;
				};
		  }
		| undefined;
	const value = contact?.value;
	return {
		contactId: value?.contactId,
		participantId: value?.participantId,
		region: value?.region,
	};
}

export type SurveyModalAttributes = {
	width?: string | number;
	height?: string | number;
	top?: string | number;
	left?: string | number;
};

function getChatConnectionFormValues(
	contextId: string,
	userEmail: string,
	isUserLoggedIn: boolean,
) {
	return {
		cst_support_forms_data_fieldDescMap: [
			'{"inquiry_category":"Inquiry Category","email":"Email","raised_obo_by":"Raised on behalf of by","language":"language","customer_working_hours":"Support Business Hours Select"}',
		],
		customer_email: [isUserLoggedIn ? userEmail : ''],
		authenticated: isUserLoggedIn ? null : ['No'],
		email: [isUserLoggedIn ? userEmail : ''],
		customer_status: ['Customer'],
		cst_support_forms_data_formKey: getContactFormKeyByPath(
			getRelativePathFromWacUrl(window.location.href),
		),
		cst_support_forms_data_formLabel: getContactFormKeyByPath(
			getRelativePathFromWacUrl(window.location.href),
		),
		cst_support_forms_data_timezone: ['GMT+6'],
		referral_url: [window.location.href],
		language: ['en'],
		is_partner_dc_exception: ['Customer not in SFDC'],
		topic: [convertUrlToSnakeCase(window.location.href)],
		summary: [convertUrlToSnakeCase(window.location.href).split('_').join(' ')],
		support_channel: ['chat'],
		inquiry_category: ['access_issues'],
		cst_support_forms_data_contextId: [contextId],
	};
}

function shouldFetchConversationsOnOpen(
	isWidgetOpen: boolean,
	wasOpenCurrent: boolean,
	clientToken: string | null,
	clientWithToken: ApolloClient<unknown> | null,
): boolean {
	return isWidgetOpen && !wasOpenCurrent && !!clientToken && !!clientWithToken;
}

function getChatConnectionMetadataPayload(
	userEmail: string | undefined,
	isUserLoggedIn: boolean,
): { flowId: string; formContext: Record<string, unknown> } {
	return {
		flowId: getContactFlowByPath(getRelativePathFromWacUrl(window.location.href)).contactFlowId,
		formContext: {
			summary: ['Chat Transcript for <email removed for privacy>'],
			details: '',
			contactEmail: isUserLoggedIn ? (userEmail ?? '') : '',
			isAuthenticated: isUserLoggedIn,
		},
	};
}

function hasCustomerConversations(
	response: {
		channelPlatform_getCustomerConversations?: {
			conversations?: unknown[];
		};
	} | null,
): boolean {
	const count = response?.channelPlatform_getCustomerConversations?.conversations?.length ?? 0;
	return count > 0;
}

function getChatWidgetViewState(
	isWidgetOpen: boolean,
	clientToken: string | null,
	chatConnectionConfig: { channelPlatform_createContact?: unknown } | null,
	isCustomerComponentLoaded: boolean,
) {
	const isChatLoaded =
		isWidgetOpen && !!clientToken && !!chatConnectionConfig && isCustomerComponentLoaded;
	return {
		isChatLoaded,
		showFloatingButton: !isChatLoaded,
		isButtonLoading:
			isWidgetOpen && (!clientToken || !chatConnectionConfig || !isCustomerComponentLoaded),
	};
}

const ChatFloatingButton = ({
	isLoading,
	onClick,
	onKeyDown,
}: {
	isLoading: boolean;
	onClick: () => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}) => {
	return (
		<div
			role="button"
			tabIndex={0}
			aria-label={isLoading ? 'Loading chat' : 'Open chat widget'}
			style={{
				position: 'fixed',
				bottom: '20px',
				right: '20px',
				zIndex: 1000,
				cursor: isLoading ? 'wait' : 'pointer',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '56px',
				height: '56px',
				borderRadius: '50%',
				backgroundColor: wacToken('color.primary.blue'),
				color: 'white',
				boxShadow: token('elevation.shadow.raised'),
			}}
			onClick={onClick}
			onKeyDown={onKeyDown}
		>
			{isLoading ? (
				<Spinner size="medium" label="Loading chat" appearance="invert" />
			) : (
				<CommentIcon label="" size="medium" color="currentColor" />
			)}
		</div>
	);
};

function useChatWidgetConnection({
	isWidgetOpen,
	contextId,
	userEmail,
	isUserLoggedIn,
	setIsCustomerComponentLoaded,
}: {
	isWidgetOpen: boolean;
	contextId: string;
	userEmail: string | undefined;
	isUserLoggedIn: boolean;
	setIsCustomerComponentLoaded: (loaded: boolean) => void;
}) {
	const [clientToken, setClientToken] = useState<string | null>(null);
	const [chatConnectionConfig, setChatConnectionConfig] = useState<{
		channelPlatform_createContact?: unknown;
	} | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);
	const [channelPlatformTokenError, setChannelPlatformTokenError] = useState<string | null>(null);
	const [customerConversationsResponse, setCustomerConversationsResponse] = useState<{
		channelPlatform_getCustomerConversations?: {
			conversations?: unknown[];
		};
	} | null>(null);

	const generateAuthHeader = (channelsIdentityHash: string) =>
		new ApolloLink((operation: Operation, forward: NextLink) => {
			operation.setContext(({ headers = {} }) => ({
				headers: {
					...headers,
					'Content-Type': 'application/json',
					'X-Client-Hash': channelsIdentityHash,
				},
			}));

			return forward(operation);
		});

	const client = useMemo(() => {
		const link = new HttpLink({
			uri:
				process.env['REACT_APP_AGG_GRAPHQL_BASE_URL'] ||
				'https://support.stg.internal.atlassian.com/gateway/api/graphql',
			credentials: 'include',
		});

		return new ApolloClient({
			link: link,
			cache: new InMemoryCache(),
		});
	}, []);

	const wasWidgetOpenRef = useRef(false);

	const clientWithToken = useMemo(() => {
		if (!clientToken) {
			return null;
		}
		const link = new HttpLink({
			uri:
				process.env['REACT_APP_AGG_GRAPHQL_BASE_URL'] ||
				'https://support.stg.internal.atlassian.com/gateway/api/graphql',
			credentials: 'include',
		});

		return new ApolloClient({
			link: ApolloLink.from([generateAuthHeader(clientToken), link]),
			cache: new InMemoryCache(),
		});
	}, [clientToken]);

	const isRefreshed = useMemo(
		() => hasCustomerConversations(customerConversationsResponse),
		[customerConversationsResponse],
	);

	const fetchCustomerConversations = useCallback(() => {
		if (!clientWithToken) return;
		const anonymousId = Cookie.readCookie('ajs_anonymous_id') || '';
		clientWithToken
			.query({
				query: getCustomerConversations,
				variables: { anonymousId },
			})
			.then((response) => {
				setCustomerConversationsResponse(response?.data ?? null);
			})
			.catch((err) => {
				console.warn('Error fetching customer conversations:', err);
				setCustomerConversationsResponse(null);
			});
	}, [clientWithToken]);

	const fetchChatConnectionConfig = useCallback(async () => {
		if (!clientWithToken) {
			console.warn('Client with token not available yet');
			return;
		}

		setLoading(true);
		const formValues = getChatConnectionFormValues(contextId, userEmail ?? '', isUserLoggedIn);

		// Dynamic import to avoid pulling @atlassiansox/analytics-web-client
		// (which calls setTimeout at module init) into the SSR bundle.
		const { getAnalyticsWebClient } = await import(
			/* webpackChunkName: "analytics-web-client" */
			'./get-analytics-web-client.ts'
		);
		const metadataPayload = getChatConnectionMetadataPayload(userEmail, isUserLoggedIn);
		const metadata = constructMetadataWithFlowId(
			{
				contactEmail: metadataPayload.formContext['contactEmail'] as string | undefined,
			},
			metadataPayload.flowId,
			getAnalyticsWebClient().getCurrentSessionId(),
		);

		clientWithToken
			.query({
				query: getChatConnectionConfigForSalesChannel,
				variables: {
					requestType: 'CHAT' as 'CHAT',
					payload: transformPayloadForChannelPlatform(formValues),
					metadata,
					attributes:
						JSON.parse(
							getContactAttributesByPath(getRelativePathFromWacUrl(window.location.href)),
						) ?? {},
					anonymousId: Cookie.readCookie('ajs_anonymous_id') || '',
				},
			})
			.then((response: { data: { channelPlatform_createContact?: unknown } }) => {
				setChatConnectionConfig(response?.data);
				setLoading(false);
			})
			.catch((err: SetStateAction<Error | null>) => {
				console.error('Error fetching chat connection config:', err);
				setError(err);
				setLoading(false);
			});
	}, [clientWithToken, contextId, isUserLoggedIn, userEmail]);

	useEffect(() => {
		if (
			shouldFetchConversationsOnOpen(
				isWidgetOpen,
				wasWidgetOpenRef.current,
				clientToken,
				clientWithToken,
			)
		) {
			wasWidgetOpenRef.current = true;
			fetchCustomerConversations();
			fetchChatConnectionConfig();
		}
		if (!isWidgetOpen) {
			wasWidgetOpenRef.current = false;
			setIsCustomerComponentLoaded(false);
		}
	}, [
		isWidgetOpen,
		clientToken,
		clientWithToken,
		fetchCustomerConversations,
		fetchChatConnectionConfig,
		setIsCustomerComponentLoaded,
	]);

	const fetchToken = useCallback(() => {
		setLoading(true);
		client
			.query({
				query: getChannelPlatformTokenByClientName,
				variables: { clientName: 'sales' },
			})
			.then(
				(response: {
					data: {
						supportInquiry_channelsIdentityHashByClientName: string;
					};
				}) => {
					const token = response.data?.supportInquiry_channelsIdentityHashByClientName;
					if (token) {
						setClientToken(token);
						setChannelPlatformTokenError(null);
					}
					setLoading(false);
				},
			)
			.catch((err: SetStateAction<Error | null>) => {
				console.error('Error fetching channel platform token:', err);
				setError(err);
				setLoading(false);
				const errorMessage = err instanceof Error ? err.message : String(err);
				setChannelPlatformTokenError(errorMessage);
			});
	}, [client]);

	return {
		clientToken,
		chatConnectionConfig,
		loading,
		error,
		channelPlatformTokenError,
		client,
		clientWithToken,
		isRefreshed,
		customerConversationsResponse,
		setClientToken,
		fetchToken,
	};
}

export const ChatWidgetProvider = ({ children }: { children: React.ReactNode }) => {
	const [isWidgetOpen, setIsWidgetOpen] = useState<boolean>(false);
	const [isCustomerComponentLoaded, setIsCustomerComponentLoaded] = useState<boolean>(false);

	const { accountId: aaid, email: userEmail, isUserLoggedIn } = useLoggedInUser();

	const contextId = useMemo(() => uuidv4(), []);

	const connection = useChatWidgetConnection({
		isWidgetOpen,
		contextId,
		userEmail,
		isUserLoggedIn,
		setIsCustomerComponentLoaded,
	});

	const {
		clientToken,
		chatConnectionConfig,
		loading,
		error,
		channelPlatformTokenError,
		isRefreshed,
		customerConversationsResponse,
	} = connection;

	const surveyModalAttributes: SurveyModalAttributes = {
		width: '80%',
		height: 500,
	};

	const omniChannelEnv: 'production' | 'staging' =
		process.env['OMNI_CHANNEL_ENVIRONMENT'] === 'production' ? 'production' : 'staging';

	const handleChatButtonClick = () => {
		const opening = !isWidgetOpen;
		setIsWidgetOpen(opening);
		if (opening) {
			connection.fetchToken();
		}
	};

	const handleChatButtonKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleChatButtonClick();
		}
	};

	const viewState = getChatWidgetViewState(
		isWidgetOpen,
		clientToken,
		chatConnectionConfig,
		isCustomerComponentLoaded,
	);

	const showChatPanel = isWidgetOpen && !!chatConnectionConfig && !!clientToken;

	const conversationId = getConversationIdFromChatConnectionConfig(chatConnectionConfig);
	const connectionConfigAttributes = getConnectionConfigAttributes(chatConnectionConfig);

	return (
		<ClientOnly>
			<>
				{children}
				{chatConnectionConfig && (
					<Suspense fallback={null}>
						<ChatWidgetCustomerJoinedAnalytics conversationId={conversationId} />
						<ChatWidgetConnectionConfigSuccessAnalytics
							contactId={connectionConfigAttributes.contactId}
							participantId={connectionConfigAttributes.participantId}
							region={connectionConfigAttributes.region}
						/>
					</Suspense>
				)}
				{channelPlatformTokenError && (
					<Suspense fallback={null}>
						<ChatWidgetTokenErrorAnalytics channelPlatformTokenError={channelPlatformTokenError} />
					</Suspense>
				)}
				{clientToken && (
					<Suspense fallback={null}>
						<ChatWidgetTokenSuccessAnalytics
							clientToken={clientToken}
							channelPlatformTokenError={channelPlatformTokenError}
						/>
					</Suspense>
				)}
				{viewState.showFloatingButton && (
					<ChatFloatingButton
						isLoading={viewState.isButtonLoading}
						onClick={handleChatButtonClick}
						onKeyDown={handleChatButtonKeyDown}
					/>
				)}
				{showChatPanel && (
					<Suspense fallback={null}>
						<ChatWidgetOmniChannelUI
							env={omniChannelEnv as ChatWidgetOmniChannelUIProps['env']}
							clientIdentityHash={clientToken || ''}
							chatContactDetails={transformChatContactDetailsForSalesChannel(chatConnectionConfig)}
							accountId={aaid || ''}
							isRefreshed={isRefreshed}
							customerConversations={customerConversationsResponse}
							loading={loading}
							error={error}
							isUserLoggedIn={isUserLoggedIn}
							surveyModalAttributes={surveyModalAttributes}
							onLoaded={() => setIsCustomerComponentLoaded(true)}
						/>
					</Suspense>
				)}
			</>
		</ClientOnly>
	);
};

export default ChatWidgetProvider;
